/**
 * Condensed node type catalog for Claude's system prompt.
 * Included when backtest tools are active.
 *
 * The app injects a registry-generated catalog via setNodeCatalog() at
 * companion bootstrap (apps/frontend graph-engine/catalog.ts). The static
 * string below is only the fallback for contexts that never inject — it is
 * known to have drifted from the registry (wrong handles, invented types)
 * and should not be extended.
 */

let injectedCatalog: string | null = null

/** Inject the registry-generated catalog (called once by the host app). */
export function setNodeCatalog(catalog: string): void {
  injectedCatalog = catalog
}

/** The catalog to put in the system prompt: injected if available, else legacy fallback. */
export function getNodeCatalog(): string {
  return injectedCatalog ?? NODE_CATALOG_PROMPT
}

export const NODE_CATALOG_PROMPT = `## available backtest node types

**Data Sources:**
- priceData — OHLCV price data | outputs: open, high, low, close, volume
- marketData — Broader market data | outputs: values
- csvData — CSV data import | outputs: values

**Indicators (Technical):**
- simplemovingaverage — SMA | params: period (default: 20) | outputs: sma
- exponentialmovingaverage — EMA | params: period (default: 20) | outputs: ema
- relativestrengthindex — RSI | params: period (default: 14) | outputs: rsi (0-100)
- macd — MACD | params: fastPeriod=12, slowPeriod=26, signalPeriod=9 | outputs: macd, signal, histogram
- bollingerbands — Bollinger Bands | params: period=20, stdDev=2 | outputs: upper, middle, lower
- averagetruerange — ATR | params: period=14 | outputs: atr
- stochasticfull — Stochastic | params: kPeriod=14, dPeriod=3, smooth=3 | outputs: k, d
- vwap — VWAP | outputs: vwap
- williamsr — Williams %R | params: period=14 | outputs: williamsr
- rateofchange — Rate of Change | params: period=12 | outputs: roc
- weightedmovingaverage — WMA | params: period=20 | outputs: wma
- averagedirectionalmovementindex — ADX | params: period=14 | outputs: adx, pdi, ndi

**Logic:**
- condition — Compare two values | params: operator (gt, gte, lt, lte, eq, neq), value | inputs: input_1, input_2 | outputs: boolean signal
- crossover — Detect when line A crosses above line B | inputs: input_1, input_2 | outputs: crossover signal
- merge — AND/OR gate | params: mode (AND, OR) | inputs: input_1, input_2 | outputs: merged signal
- barDirection — Bar direction (up/down) | outputs: direction signal
- barsConditionCounter — Count consecutive bars matching condition | params: count | outputs: signal

**Math:**
- add, subtract, multiply, divide — Binary operations | inputs: input_1, input_2
- percentage — Percentage of value | params: percentage
- slope — Rate of change slope | params: period
- average — Rolling average | params: period
- sum — Rolling sum | params: period

**Strategy (Entry/Exit):**
- entryRules — Entry signal logic | inputs: signal | outputs: entry
- exitRules — Exit signal logic | inputs: signal | outputs: exit
- positionSizing — Position size calculator | params: method, amount
- target — Profit target | params: percentage
- multiTarget — Multiple profit targets | params: targets array
- stop — Stop loss | params: percentage
- trailingStop — Trailing stop | params: percentage, activation
- multiStop — Multiple stop levels
- trade — Full trade node (entry + exit + sizing + targets + stops)
- simpleTrade — Simple trade (entry only, auto-exit)

**Filters:**
- timeFilter — Filter by time of day | params: startTime, endTime
- cooldown — Wait period between signals | params: bars
- volumeFilter — Volume threshold | params: multiplier
- priceChange — Price change filter | params: percentage
- rolling — Rolling window calculation | params: period

**Debug:**
- debugValues — Output node values for inspection
- debugChart — Overlay values on chart
- signalGenerator — Generate test signals

## Building strategies with create_strategy_from_recipe (PREFERRED)

Use create_strategy_from_recipe with a compact recipe array instead of calling add_node/connect_nodes repeatedly. Each step specifies a node, its parameters, and explicit input connections.

Recipe step format:
{ "id": "unique_name", "type": "nodeType", "params": { "key": value }, "inputs": { "targetHandle": "sourceId.outputHandle" } }

Connection rules:
- "sourceId.outputHandle" connects to the specified output (e.g. "fast_sma.sma")
- For OHLCV fields: "price.close", "price.volume" etc. (auto-resolves to ohlcv output + field selection)
- For static inputs: omit from inputs and set the discriminated param (e.g. "inputBType": "static") in params

Example — SMA crossover:
[
  { "id": "price", "type": "priceData" },
  { "id": "fast_sma", "type": "simplemovingaverage", "params": { "period": 10 }, "inputs": { "input_1": "price.close" } },
  { "id": "slow_sma", "type": "simplemovingaverage", "params": { "period": 50 }, "inputs": { "input_1": "price.close" } },
  { "id": "cross", "type": "crossover", "inputs": { "input_1": "fast_sma.sma", "input_2": "slow_sma.sma" } },
  { "id": "entry", "type": "entryRules", "inputs": { "signal": "cross.crossover" } },
  { "id": "trade", "type": "simpleTrade", "inputs": { "input_1": "entry.entry" } }
]

Pattern:
1. Start with priceData as the data source
2. Connect indicators to priceData outputs using OHLCV field references
3. Use condition/crossover/merge for signal logic
4. Feed signals into entryRules → trade/simpleTrade
5. Add target/stop nodes for risk management
`
